import { DesignObject, Location } from '../types/interfaces';

export type FacilityLocationRef = Partial<
  Pick<DesignObject, 'locationId' | 'name' | 'abbreviation' | 'type'>
> & {
  /** Tolerate legacy / alternate keys from older saved layouts. */
  location_id?: number | string;
  LocationId?: number | string;
};

function norm(s: string | null | undefined): string {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** Taproom location (sold, tax paid — never brewery On Hand). */
export function isTaproomLocation(loc: Location | null | undefined): boolean {
  if (!loc) return false;
  if (loc.taproom) return true;
  if (String(loc.storageRole || '').toLowerCase() === 'taproom') return true;
  return /^TAPROOM/i.test(String(loc.siteId || ''));
}

/** Location belongs to the site, or is that brewery site's linked taproom. */
function inSiteOrLinked(l: Location, siteId?: string | null): boolean {
  if (!siteId) return true;
  return String(l.siteId) === String(siteId) || (!!l.linkedSiteId && String(l.linkedSiteId) === String(siteId));
}

/** Read locationId from common key variants on saved design JSON. */
export function readShapeLocationId(obj: FacilityLocationRef | null | undefined): number | null {
  if (!obj) return null;
  const raw = obj.locationId ?? obj.location_id ?? obj.LocationId;
  if (raw == null || raw === ('' as unknown as number)) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function matchByLabel(label: string, siteLocs: Location[]): Location | null {
  if (!label) return null;

  const exactName = siteLocs.find((l) => norm(l.name) === label);
  if (exactName) return exactName;

  const exactAbbr = siteLocs.find((l) => norm(l.abbreviation) === label);
  if (exactAbbr) return exactAbbr;

  // "Cold Storage" → "Madison Cold Storage" / "Athens Cold Storage"
  const suffix = siteLocs.find((l) => {
    const ln = norm(l.name);
    return ln.endsWith(` ${label}`) || ln.endsWith(label);
  });
  if (suffix) return suffix;

  if (label.length >= 4) {
    const contains = siteLocs.find((l) => norm(l.name).includes(label));
    if (contains) return contains;
  }

  return null;
}

function matchColdOrCooler(label: string, siteLocs: Location[]): Location | null {
  if (!label) return null;

  // Prod seed: Madison Cold Storage abbreviation is "Beer Cooler" (not COLD).
  if (
    label === 'cold' ||
    label === 'beer cooler' ||
    /\bcold\b/.test(label) ||
    label.includes('cold storage')
  ) {
    const byRole = siteLocs.find((l) => String(l.storageRole || '').toLowerCase() === 'cold');
    if (byRole) return byRole;
    const cold = siteLocs.find((l) => /cold\s*storage/i.test(l.name || ''));
    if (cold) return cold;
  }

  if (
    label === 'mbc' ||
    label === 'abc' ||
    label.includes('brewery cooler') ||
    (label.includes('cooler') && !label.includes('taproom') && !label.includes('cold'))
  ) {
    const byRole = siteLocs.find((l) => String(l.storageRole || '').toLowerCase() === 'fg_cooler');
    if (byRole) return byRole;
    const cooler = siteLocs.find((l) => /brewery\s*cooler/i.test(l.name || ''));
    if (cooler) return cooler;
  }

  return null;
}

/**
 * Resolve a Facility View / Designer shape to a catalog location.
 *
 * Prod layouts often label Storage rectangles with a short room name (or put
 * that label only in `abbreviation`, which is what the canvas paints) and no
 * locationId. Match by id first, then name/abbr as labels, then cold/cooler
 * heuristics (including storageRole).
 */
export function resolveFacilityLocation(
  obj: FacilityLocationRef | null | undefined,
  locations: Location[],
  siteId?: string | null
): Location | null {
  if (!obj) return null;
  const id = readShapeLocationId(obj);
  if (id != null) {
    // Linked taproom locations (other siteId) resolve by explicit id only.
    const byId = (locations || []).find((l) => Number(l.locationId) === id && inSiteOrLinked(l, siteId));
    if (byId) return byId;
  }

  // Name / abbreviation heuristics stay within the site's own locations so an
  // existing "OBB"-labelled shape can't silently start pointing at the taproom.
  const siteLocs = (locations || []).filter(
    (l) => !siteId || String(l.siteId) === String(siteId)
  );
  if (!siteLocs.length) return null;

  const name = norm(obj.name);
  const abbr = norm(obj.abbreviation);
  // Canvas paints abbreviation — treat both fields as label candidates.
  const labels = Array.from(new Set([name, abbr].filter(Boolean)));

  for (const label of labels) {
    const hit = matchByLabel(label, siteLocs);
    if (hit) return hit;
  }

  for (const label of labels) {
    const hit = matchColdOrCooler(label, siteLocs);
    if (hit) return hit;
  }

  return null;
}

/** Attach resolved locationId onto design objects for the active site (non-mutating copy). */
export function enrichDesignObjectsWithLocations(
  objects: DesignObject[],
  locations: Location[],
  siteId?: string | null
): DesignObject[] {
  return (objects || []).map((obj) => {
    const existingId = readShapeLocationId(obj);
    if (existingId != null) {
      const stillValid = locations.some(
        (l) => Number(l.locationId) === existingId && inSiteOrLinked(l, siteId)
      );
      if (stillValid) {
        return existingId === Number(obj.locationId) ? obj : { ...obj, locationId: existingId };
      }
    }
    const resolved = resolveFacilityLocation(obj, locations, siteId);
    if (!resolved) return obj;
    return {
      ...obj,
      locationId: Number(resolved.locationId),
      // Keep shape label if present; otherwise adopt catalog name/abbr
      name: obj.name || resolved.name,
      abbreviation:
        obj.abbreviation ||
        resolved.abbreviation ||
        String(resolved.name || '')
          .slice(0, 3)
          .toUpperCase(),
    };
  });
}
